import { lazy, memo, useEffect } from "react";

import { useSetState } from "~/hooks/useSetState";
import { getAssetUrl } from "~/utils/url";
import { useLabels } from "~/hooks/useLabels";
import { useUserContext } from "~/hooks/useUserContext";

import { When } from "../when/When";

export interface LoginModalProps {
  isOpen?: boolean;
  onRequestClose?: () => void;
}

interface LoginModalState {
  isMounted: boolean;
  isOpen: boolean;
}

const OnboardingModal = lazy(() =>
  import("../onboardingModal/OnboardingModal").then(({ OnboardingModal }) => ({
    default: OnboardingModal,
  })),
);

/**
 * A lazy-loaded modal that prompts the user to sign in or sign up.
 */
export const LoginModal = memo(function LoginModal({
  isOpen = false,
  onRequestClose,
}: LoginModalProps) {
  const { t } = useLabels();

  const { goToLogin, goToRegister } = useUserContext();

  const [state, setState] = useSetState<LoginModalState>({
    isMounted: isOpen,
    isOpen,
  });

  useEffect(() => {
    setState((prev) => ({
      isMounted: isOpen || prev.isMounted,
      isOpen,
    }));
  }, [isOpen, setState]);

  return (
    <When condition={state.isMounted}>
      <OnboardingModal
        isOpen={state.isOpen}
        artwork={getAssetUrl("/images/classes-on-every-device-wide.png")}
        title={t("unauthenticated.title")}
        description={t("unauthenticated.description")}
        primaryActionLabel={t("unauthenticated.cta.primary")}
        secondaryActionLabel={t("unauthenticated.cta.secondary")}
        onRequestClose={onRequestClose}
        onUnmount={() => setState({ isMounted: false })}
        onPrimaryAction={goToRegister}
        onSecondaryAction={goToLogin}
      />
    </When>
  );
});
